import { invalidate } from '@react-three/fiber';
import { useCallback, useEffect, useRef } from 'react';

import { updateAttributeRange } from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { usePoiEventHandler } from 'app/modules/wallforming/components/scenes/objects/pois/hooks/usePoiEventHandler';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';
import type { PoiSelection } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiSelection';
import type { BatchedMesh, BufferGeometry } from 'three';

interface PoiBatchedMeshHandlerProps {
    entries: PoiBatchedEntry[];
    geometriesByType: Map<POIFilterType, BufferGeometry>;
}

const updatedIndices = new Set<number>();
const map = new Map<POIFilterType, number>();

function isPoiVisible(filterType: POIFilterType | undefined): boolean {
    return filterType !== undefined && !usePlacementStore.getState().hiddenPOIFilterTypes.includes(filterType);
}

export const usePoiBatchedMeshHandler = ({ entries, geometriesByType }: PoiBatchedMeshHandlerProps) => {
    const meshRef = useRef<BatchedMesh | null>(null);
    const previousMeshRef = useRef<BatchedMesh | null>(null);
    const geometryIdsByTypeRef = useRef<Map<POIFilterType, number>>(new Map());
    const instanceIdByEntryIndexRef = useRef<number[]>([]);
    const entryIndexByInstanceIdRef = useRef<Map<number, number>>(new Map());
    const hoveredBatchIdRef = useRef<number | null>(null);
    const poiSelectionRef = useRef<PoiSelection>(usePlacementStore.getState().poiSelection.current);

    const { handleOnPoiSelection, handleOnPointerMove, handleOnPointerLeave } = usePoiEventHandler({
        entries,
        meshRef,
        hoveredBatchIdRef,
        instanceIdByEntryIndexRef,
        entryIndexByInstanceIdRef
    });

    const initGeometries = useCallback(
        (mesh: BatchedMesh) => {
            map.clear();
            for (const [filterType, geometry] of geometriesByType) {
                map.set(filterType, mesh.addGeometry(geometry));
            }
            geometryIdsByTypeRef.current = map;
        },
        [geometriesByType]
    );

    const initInstances = useCallback(
        (mesh: BatchedMesh) => {
            const geometryIds = geometryIdsByTypeRef.current;
            instanceIdByEntryIndexRef.current = [];
            entryIndexByInstanceIdRef.current = new Map();

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                const geometryId = entry.filterType !== undefined ? geometryIds.get(entry.filterType) : undefined;

                if (geometryId === undefined) {
                    continue;
                }

                const instanceId = mesh.addInstance(geometryId);
                instanceIdByEntryIndexRef.current[i] = instanceId;
                entryIndexByInstanceIdRef.current.set(instanceId, i);
                mesh.setMatrixAt(instanceId, entry.matrix);
                mesh.setColorAt(instanceId, entry.defaultColor);
                mesh.setVisibleAt(instanceId, isPoiVisible(entry.filterType));
            }
        },
        [entries]
    );

    // Handle POI initialization
    const handleMeshRef = useCallback(
        (mesh: BatchedMesh | null) => {
            meshRef.current = mesh;
            if (!mesh || mesh === previousMeshRef.current) {
                return;
            }

            previousMeshRef.current = mesh;
            initGeometries(mesh);
            initInstances(mesh);
        },
        [initGeometries, initInstances]
    );

    // Synchronize matrices when POI entries change
    const syncEntries = useCallback(
        (mesh: BatchedMesh | null) => {
            if (!mesh || mesh.instanceCount <= 0) {
                return;
            }

            const geometryIds = geometryIdsByTypeRef.current;
            if (geometryIds.size === 0) {
                return;
            }

            const instanceIdByEntryIndex = instanceIdByEntryIndexRef.current;
            if (instanceIdByEntryIndex.length === 0) {
                return;
            }

            for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
                const instanceId = instanceIdByEntryIndex[entryIndex];
                if (instanceId === undefined) {
                    continue;
                }

                const entry = entries[entryIndex];
                const geometryId = entry.filterType !== undefined ? geometryIds.get(entry.filterType) : undefined;
                if (geometryId === undefined) {
                    continue;
                }

                const isSelected = poiSelectionRef.current.index === entry.poiIndex;

                mesh.setGeometryIdAt(instanceId, geometryId);
                mesh.setMatrixAt(instanceId, entry.matrix);
                mesh.setColorAt(instanceId, isSelected ? entry.selectedColor : entry.defaultColor);
                mesh.setVisibleAt(instanceId, isPoiVisible(entry.filterType));
            }
        },
        [entries]
    );

    useEffect(() => {
        const mesh = meshRef.current;
        if (!mesh) {
            return;
        }

        syncEntries(mesh);
    }, [entries, syncEntries]);

    // Update POI hover highlight
    useEffect(() => {
        const subscribedPoiSelection = usePlacementStore.subscribe(
            (state) => state.hiddenPOIFilterTypes,
            (newHiddenPOIFilterTypes) => {
                const mesh = meshRef.current;
                if (!mesh) {
                    return;
                }

                const hiddenTypes = new Set(newHiddenPOIFilterTypes);
                const instanceIdByEntryIndex = instanceIdByEntryIndexRef.current;

                for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
                    const instanceId = instanceIdByEntryIndex[entryIndex];
                    if (instanceId === undefined) {
                        continue;
                    }

                    const entry = entries[entryIndex];
                    mesh.setVisibleAt(instanceId, entry.filterType !== undefined && !hiddenTypes.has(entry.filterType));
                }

                invalidate();
            }
        );

        return () => subscribedPoiSelection();
    }, [entries]);

    // Update POI selection highlight
    useEffect(() => {
        const subscribedPoiSelection = usePlacementStore.subscribe(
            (state) => state.poiSelection.current,
            (newPoiSelection) => {
                poiSelectionRef.current = newPoiSelection;

                const mesh = meshRef.current;
                if (!mesh) {
                    return;
                }

                updatedIndices.clear();
                const instanceIdByEntryIndex = instanceIdByEntryIndexRef.current;

                for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
                    const instanceId = instanceIdByEntryIndex[entryIndex];
                    if (instanceId === undefined) {
                        continue;
                    }

                    const entry = entries[entryIndex];
                    const isSelected = newPoiSelection.index === entry.poiIndex;
                    const isHovered = hoveredBatchIdRef.current === instanceId;
                    mesh.setColorAt(
                        instanceId,
                        isSelected
                            ? entry.selectedColor
                            : isHovered
                              ? (entry.hoverColor ?? entry.selectedColor)
                              : entry.defaultColor
                    );
                    updatedIndices.add(instanceId);
                }

                updateAttributeRange(mesh, updatedIndices, 'colorAttribute', 4);
                invalidate();
            }
        );

        return () => subscribedPoiSelection();
    }, [entries]);

    // Handle geometry disposal
    useEffect(() => {
        const mesh = meshRef.current;
        return () => {
            mesh?.geometry?.dispose();
            mesh?.material?.dispose();
        };
    }, []);

    return {
        meshRef,
        handleMeshRef,
        handleOnPoiSelection,
        handleOnPointerMove,
        handleOnPointerLeave,
        hoveredBatchIdRef,
        instanceIdByEntryIndexRef
    };
};
